/* ============================================================
   NICE WINE — custom-scripts.js
   Mega menu: open/close + panel switching.

   Dependencies: GSAP (gsap, core only — no plugins needed).
   jQuery is available but not used here — GSAP handles all
   animation and vanilla JS handles all DOM interaction.
   No $ wrapper needed; everything runs after DOMContentLoaded.
   ============================================================ */

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {

    /* ── Product metafield modal (single overlay) ─────────────── */
    const productModalOverlay = document.querySelector('[data-product-modal-overlay]');
    const productModalTriggers = document.querySelectorAll('[data-product-card-modal-trigger]');

    if (productModalOverlay && productModalTriggers.length) {
      const productModalImage = productModalOverlay.querySelector('[data-product-modal-image]');
      const productModalLabel = productModalOverlay.querySelector('[data-product-modal-label]');
      const productModalClose = productModalOverlay.querySelector('[data-product-modal-close]');

      function setProductModalOpen(open) {
        productModalOverlay.classList.toggle('is-open', open);
        productModalOverlay.setAttribute('aria-hidden', open ? 'false' : 'true');
        document.body.classList.toggle('has-product-modal-open', open);
      }

      function closeProductModal() {
        setProductModalOpen(false);
      }

      function openProductModal(triggerEl) {
        if (!triggerEl) return;
        const label = triggerEl.dataset.productModalLabel || '';
        const imageUrl = triggerEl.dataset.productModalImage || '';

        if (productModalImage) {
          productModalImage.src = imageUrl;
          productModalImage.alt = label;
        }
        if (productModalLabel) productModalLabel.textContent = label;

        setProductModalOpen(true);

        if (productModalClose && typeof productModalClose.focus === 'function') {
          productModalClose.focus();
        }
      }

      productModalTriggers.forEach(function (triggerEl) {
        triggerEl.addEventListener('click', function (e) {
          e.preventDefault();
          openProductModal(triggerEl);
        });
      });

      productModalOverlay.addEventListener('click', function (e) {
        /* Close only when user clicks the overlay backdrop */
        if (e.target === productModalOverlay) closeProductModal();
      });

      if (productModalClose) {
        productModalClose.addEventListener('click', function (e) {
          e.preventDefault();
          closeProductModal();
        });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        closeProductModal();
      });
    }

    /* ── Guard: no mega menu on this page ───────────────────── */
    const header   = document.querySelector('.header-section');
    const megaMenu = document.getElementById('mega-menu');
    if (!header || !megaMenu) return;

    const trigger        = header.querySelector('.is-mega-trigger');
    const hamburger      = header.querySelector('.header-hamburger');
    const wineBtns       = megaMenu.querySelectorAll('.mega-menu-wine-btn');
    const secondaryLinks = megaMenu.querySelectorAll('.mega-menu-secondary-link');
    const panels         = megaMenu.querySelectorAll('.mega-menu-panel');

    if (!trigger || !panels.length) return;

    function isMobile() {
      return window.matchMedia('(max-width: 768px)').matches;
    }


    /* ── State ──────────────────────────────────────────────── */
    let isOpen      = false;
    let activeIndex = 0;    /* 0-based index of the visible panel */
    let openTimer   = null;
    let closeTimer  = null;


    /* ── GSAP initial setup ─────────────────────────────────── */

    /* Mega menu: start fully hidden — dropdown on desktop, slide from left on mobile */
    if (isMobile()) {
      gsap.set(megaMenu, { autoAlpha: 1, xPercent: -100 });
    } else {
      gsap.set(megaMenu, { autoAlpha: 0, y: -12 });
    }

    /* All panels hidden except index 0 */
    panels.forEach(function (panel, i) {
      gsap.set(panel, { display: i === 0 ? 'block' : 'none' });
    });

    /* All product cards: start at rest position but invisible.
       GSAP animates them in; clearProps hands opacity back to CSS
       afterwards so :hover { opacity: 0.75 } works again. */
    getAllCards().forEach(function (card) {
      gsap.set(card, { opacity: 0, y: 18 });
    });

    /* Wine names: opacity driven by CSS class (.is-active = 1, else 0.5).
       We only use GSAP for the entrance slide-in; clearProps hands
       control back to CSS once done. */


    /* ── Helpers ────────────────────────────────────────────── */

    function getAllCards() {
      return Array.from(megaMenu.querySelectorAll('.mega-menu-product-card'));
    }

    function getPanelCards(panel) {
      return panel.querySelectorAll('.mega-menu-product-card');
    }

    function setActiveBtn(index) {
      wineBtns.forEach(function (btn) { btn.classList.remove('is-active'); });
      if (wineBtns[index]) wineBtns[index].classList.add('is-active');
    }

    /* Dim all left-column items to 0.5, except the one being hovered.
       hoveredEl can be a wine button or a secondary link. */
    function dimLeftCol(hoveredEl) {
      wineBtns.forEach(function (btn) {
        gsap.to(btn, { opacity: btn === hoveredEl ? 1 : 0.5, duration: 0.15, ease: 'power1.out' });
      });
      secondaryLinks.forEach(function (link) {
        gsap.to(link, { opacity: link === hoveredEl ? 1 : 0.5, duration: 0.15, ease: 'power1.out' });
      });
    }

    /* Reset all left-column items to full opacity. */
    function resetLeftCol() {
      wineBtns.forEach(function (btn) {
        gsap.to(btn, { opacity: 1, duration: 0.15, ease: 'power1.out' });
      });
      secondaryLinks.forEach(function (link) {
        gsap.to(link, { opacity: 1, duration: 0.15, ease: 'power1.out' });
      });
    }


    /* ── Open mega menu ─────────────────────────────────────── */

    function openMega() {
      clearTimeout(closeTimer);
      if (isOpen) return;
      isOpen = true;

      /* Accessibility */
      trigger.setAttribute('aria-expanded', 'true');
      megaMenu.setAttribute('aria-hidden', 'false');
      megaMenu.classList.add('is-open');
      if (hamburger) {
        hamburger.setAttribute('aria-expanded', 'true');
        hamburger.classList.add('is-open');
        hamburger.setAttribute('aria-label', 'Close navigation menu');
      }
      if (isMobile()) {
        document.body.style.overflow = 'hidden';
      }

      const activePanel = panels[activeIndex];
      const activeCards = getPanelCards(activePanel);

      /* Reset card positions before animating in */
      gsap.set(activeCards, { opacity: 0, y: 18 });

      const tl = gsap.timeline();

      /* 1. Animate menu in — slide from left on mobile, drop down on desktop */
      if (isMobile()) {
        tl.to(megaMenu, {
          xPercent: 0,
          duration: 0.3,
          ease: 'power2.out'
        });
      } else {
        tl.to(megaMenu, {
          autoAlpha: 1,
          y: 0,
          duration: 0.22,
          ease: 'power2.out'
        });
      }

      /* 2. Stagger in product cards — clearProps so CSS hover works after */
      tl.to(activeCards, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        stagger: 0.08,
        ease: 'power2.out',
        clearProps: 'opacity,y,transform'
      }, 0.1);
    }


    /* ── Close mega menu ────────────────────────────────────── */

    function closeMega() {
      clearTimeout(openTimer);
      if (!isOpen) return;
      isOpen = false;

      trigger.setAttribute('aria-expanded', 'false');
      if (hamburger) {
        hamburger.setAttribute('aria-expanded', 'false');
        hamburger.classList.remove('is-open');
        hamburger.setAttribute('aria-label', 'Open navigation menu');
      }
      if (isMobile()) {
        document.body.style.overflow = '';
      }

      if (isMobile()) {
        gsap.to(megaMenu, {
          xPercent: -100,
          duration: 0.25,
          ease: 'power2.in',
          onComplete: function () {
            megaMenu.setAttribute('aria-hidden', 'true');
            megaMenu.classList.remove('is-open');
            resetCardsAndLeftCol();
          }
        });
      } else {
        gsap.to(megaMenu, {
          autoAlpha: 0,
          y: -10,
          duration: 0.18,
          ease: 'power2.in',
          onComplete: function () {
            megaMenu.setAttribute('aria-hidden', 'true');
            megaMenu.classList.remove('is-open');
            resetCardsAndLeftCol();
          }
        });
      }
    }

    function resetCardsAndLeftCol() {
      getAllCards().forEach(function (card) {
        gsap.set(card, { opacity: 0, y: 18 });
      });
      gsap.set(Array.from(wineBtns), { opacity: 1 });
      gsap.set(Array.from(secondaryLinks), { opacity: 1 });
    }


    /* ── Switch wine panel ──────────────────────────────────── */

    function switchPanel(newIndex) {
      if (newIndex === activeIndex) return;

      const oldPanel    = panels[activeIndex];
      const newPanel    = panels[newIndex];
      const oldCards    = getPanelCards(oldPanel);
      const newCards    = getPanelCards(newPanel);

      /* Kill any in-progress card tweens so rapid hovers don't stack */
      gsap.killTweensOf(oldCards);
      gsap.killTweensOf(newCards);

      /* Update button active state (CSS handles opacity) */
      setActiveBtn(newIndex);
      activeIndex = newIndex;

      const tl = gsap.timeline();

      /* 1. Fade old cards out quickly */
      tl.to(oldCards, {
        opacity: 0,
        y: -10,
        duration: 0.12,
        ease: 'power2.in',
        onComplete: function () {
          /* Swap display */
          gsap.set(oldPanel, { display: 'none' });
          oldPanel.classList.remove('is-active');
          gsap.set(newPanel, { display: 'block' });
          newPanel.classList.add('is-active');
          /* Park new cards ready to animate in */
          gsap.set(newCards, { opacity: 0, y: 18 });
        }
      });

      /* 2. Stagger new cards in — clearProps returns opacity to CSS */
      tl.to(newCards, {
        opacity: 1,
        y: 0,
        duration: 0.28,
        stagger: 0.08,
        ease: 'power2.out',
        clearProps: 'opacity,y,transform'
      }, '+=0.02');
    }


    /* ── Event listeners ────────────────────────────────────── */

    /* Trigger — hover with 80ms intent delay */
    trigger.addEventListener('mouseenter', function () {
      clearTimeout(closeTimer);
      openTimer = setTimeout(openMega, 80);
    });

    /* Click — toggle for keyboard / touch users */
    trigger.addEventListener('click', function () {
      if (isOpen) closeMega();
      else openMega();
    });

    /* Hamburger — open/close mega menu on mobile only */
    if (hamburger) {
      hamburger.addEventListener('click', function () {
        if (!isMobile()) return;
        if (isOpen) closeMega();
        else openMega();
      });
    }

    /* Keep open when hovering over the mega menu itself */
    megaMenu.addEventListener('mouseenter', function () {
      clearTimeout(closeTimer);
    });

    /* Close when mouse leaves the entire header (bar + mega menu) */
    header.addEventListener('mouseleave', function () {
      clearTimeout(openTimer);
      closeTimer = setTimeout(closeMega, 180);
    });

    /* Re-enter header cancels the close timer */
    header.addEventListener('mouseenter', function () {
      clearTimeout(closeTimer);
    });

    /* Wine button hover — switch panel + dim others */
    wineBtns.forEach(function (btn, i) {
      btn.addEventListener('mouseenter', function () {
        dimLeftCol(btn);
        switchPanel(i);
      });

      /* Click a wine name → navigate to its collection page */
      btn.addEventListener('click', function () {
        const href = btn.dataset.href;
        if (href) window.location.href = href;
      });
    });

    /* Secondary link hover — dim all others, this link stays full opacity */
    secondaryLinks.forEach(function (link) {
      link.addEventListener('mouseenter', function () {
        dimLeftCol(link);
      });
    });

    /* Mouse leaves the left nav column → reset all to full opacity */
    const megaNav = megaMenu.querySelector('.mega-menu-nav');
    if (megaNav) {
      megaNav.addEventListener('mouseleave', function () {
        resetLeftCol();
      });
    }

    /* Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        closeMega();
        if (hamburger && isMobile()) hamburger.focus();
        else trigger.focus();
      }
    });

    /* Resize: close if switching to desktop while menu is open */
    window.addEventListener('resize', function () {
      if (!isMobile() && isOpen) {
        closeMega();
      }
    });

    /* Click outside header closes */
    document.addEventListener('click', function (e) {
      if (isOpen && !header.contains(e.target)) {
        closeMega();
      }
    });

  }); /* end DOMContentLoaded */


  if (!window.gsap) {
    return;
  }

  if (window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
  }

function initStaggerCardReveal() {
  if (!window.ScrollTrigger) return;

  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReducedMotion = reduceMotionQuery.matches;

  document.querySelectorAll('[data-stagger-section]').forEach(section => {
    const items = Array.from(section.querySelectorAll('[data-stagger-item]'));
    if (!items.length) return;

    if (prefersReducedMotion) {
      gsap.set(items, { clearProps: 'all' });
      return;
    }

    gsap.set(items, { opacity: 0, y: 28 });

    ScrollTrigger.batch(items, {
      start: 'top 88%',
      once: true,
      onEnter: batch => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.65,
          ease: 'power2.out',
          stagger: 0.16,
          overwrite: true
        });
      }
    });
  });
}

function initRevealAfterSplit() {
  if (!window.ScrollTrigger) return;

  document.querySelectorAll('[data-reveal-after-split]').forEach(el => {
    const trigger = el.closest('.shopify-section, .section, section') || el;
    const delay = parseFloat(el.dataset.revealDelay || '0.65');

    if (!trigger) return;

    gsap.from(el, {
      y: 36,
      opacity: 0,
      duration: 0.55,
      delay: Number.isNaN(delay) ? 0.65 : delay,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: trigger,
        start: 'top 85%',
        once: true
      }
    });
  });
}

function initBrandIconScrollSpin() {
  if (!window.ScrollTrigger) return;

  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotionQuery.matches) return;

  document.querySelectorAll('.brand-icon').forEach(icon => {
    gsap.to(icon, {
      rotation: 40,
      ease: 'none',
      scrollTrigger: {
      trigger: icon,
      start: 'top bottom',
      end: 'bottom top',
        scrub: 2
      }
    });
  });
}

function initLenisSmoothScroll() {
  if (!window.Lenis || !window.ScrollTrigger) return;
  if (window.__lenisInstance) return;

  const lenis = new window.Lenis({
    duration: 0.9,
    smoothWheel: true,
    smoothTouch: false
  });
  window.__lenisInstance = lenis;

  lenis.on('scroll', ScrollTrigger.update);

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  ScrollTrigger.refresh();
}

document.addEventListener("DOMContentLoaded", () => {
  initLenisSmoothScroll()
  initStaggerCardReveal()
  initRevealAfterSplit()
  initBrandIconScrollSpin()
});


})();





